<template>
  <el-dialog
    :model-value="modelValue"
    title="💬 시스템 제보"
    :fullscreen="isMobile"
    :width="isMobile ? undefined : '540px'"
    @update:model-value="$emit('update:modelValue', $event)"
    @open="onOpen"
  >
    <el-form :model="form" label-position="top" @paste="onPaste">
      <el-form-item label="유형" required>
        <el-radio-group v-model="form.feedback_type">
          <el-radio-button label="IMPROVEMENT">개선 요청</el-radio-button>
          <el-radio-button label="BUG">오류 신고</el-radio-button>
          <el-radio-button label="QUESTION">질문</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <el-form-item label="제목" required>
        <el-input v-model="form.title" maxlength="200" placeholder="한 줄 요약" />
      </el-form-item>

      <!-- 내용: 유형별 구조화 템플릿 ↔ 자유 서술 전환 -->
      <div class="content-head">
        <span class="content-label">내용 <span class="req">*</span></span>
        <el-link type="primary" :underline="false" class="mode-toggle" @click="toggleFreeForm">
          {{ freeForm ? '📋 템플릿으로 작성' : '✏️ 자유 서술로 전환' }}
        </el-link>
      </div>

      <template v-if="!freeForm">
        <el-form-item
          v-for="f in currentTemplate"
          :key="form.feedback_type + f.key"
          :label="f.required ? f.label : f.label + ' (선택)'"
          :required="f.required"
          class="tpl-item"
        >
          <el-input
            v-model="fields[f.key]"
            type="textarea"
            :rows="f.rows"
            :placeholder="f.placeholder"
          />
        </el-form-item>
      </template>

      <el-form-item v-else>
        <el-input
          v-model="form.content"
          type="textarea"
          :rows="7"
          placeholder="내용을 자유롭게 입력하세요"
        />
      </el-form-item>

      <el-form-item :label="`스크린샷 (선택, 최대 ${MAX_IMAGES}장 · 붙여넣기 Ctrl+V 가능)`">
        <div class="img-area">
          <div v-if="images.length" class="img-list">
            <div v-for="(img, i) in images" :key="img.url" class="img-thumb">
              <el-image :src="img.url" fit="cover" class="thumb" :preview-src-list="images.map(x => x.url)" :initial-index="i" />
              <button type="button" class="img-del" title="삭제" @click="removeImage(i)">×</button>
            </div>
          </div>
          <el-button v-if="images.length < MAX_IMAGES" size="small" @click="fileInput?.click()">+ 이미지 추가</el-button>
          <span v-if="images.length" class="img-count">{{ images.length }}/{{ MAX_IMAGES }}</span>
          <input ref="fileInput" type="file" accept="image/*" multiple style="display:none" @change="onFileChange" />
        </div>
      </el-form-item>

      <el-form-item>
        <el-checkbox v-model="includeContext">
          현재 화면 정보 포함 ({{ currentRoute }})
        </el-checkbox>
      </el-form-item>
    </el-form>

    <template #footer>
      <div :style="isMobile ? 'display:flex;gap:8px' : ''">
        <el-button @click="$emit('update:modelValue', false)" :style="isMobile ? 'flex:1;margin:0' : ''">취소</el-button>
        <el-button type="primary" :loading="submitting" @click="submit" :style="isMobile ? 'flex:1;margin:0' : ''">제출</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'

defineProps({
  modelValue: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'submitted'])

const route = useRoute()
const router = useRouter()
const isMobile = ref(window.innerWidth <= 768)

const MAX_IMAGES = 5
const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB (서버 정책과 동일)

// ── 유형별 구조화 템플릿 ──
const TEMPLATES = {
  BUG: [
    { key: 'steps', label: '어떤 작업을 했나요?', rows: 3, required: true,
      placeholder: '예) 자금일보 > 7/8 보고서 열기 > 저장 클릭' },
    { key: 'actual', label: '실제 결과 (오류 메시지 등)', rows: 2, required: true,
      placeholder: '예) "저장에 실패했습니다" 메시지가 뜨고 화면이 멈춤' },
    { key: 'expected', label: '기대한 결과', rows: 2, required: false,
      placeholder: '예) 정상 저장되고 목록 화면으로 이동' },
  ],
  IMPROVEMENT: [
    { key: 'current', label: '현재 방식 / 불편한 점', rows: 3, required: true,
      placeholder: '예) 지출결의서 작성 시 거래처를 매번 검색해야 함' },
    { key: 'proposal', label: '개선 요청 내용', rows: 3, required: true,
      placeholder: '예) 최근 사용한 거래처가 상단에 먼저 나오면 좋겠음' },
    { key: 'benefit', label: '기대 효과', rows: 2, required: false,
      placeholder: '예) 건당 입력 시간 단축' },
  ],
  QUESTION: [
    { key: 'question', label: '궁금한 내용', rows: 3, required: true,
      placeholder: '예) 휴가 신청 후 결재선을 바꿀 수 있나요?' },
    { key: 'context', label: '시도해 본 것 / 참고 사항', rows: 2, required: false,
      placeholder: '예) 신청 내역에서 수정 버튼을 찾아봤는데 없었음' },
  ],
}

const form = ref({
  feedback_type: 'IMPROVEMENT',
  title: '',
  content: '',
})
const fields = ref({})
const freeForm = ref(false)
const includeContext = ref(true)
const images = ref([]) // { file, url }
const fileInput = ref(null)
const submitting = ref(false)

const currentRoute = computed(() => route.fullPath)
const currentTemplate = computed(() => TEMPLATES[form.value.feedback_type] || [])

const onOpen = () => {
  isMobile.value = window.innerWidth <= 768
}

// ── 템플릿 → 본문 합성 ──
const composeContent = () => {
  return currentTemplate.value
    .map((f) => {
      const v = (fields.value[f.key] || '').trim()
      return v ? `[${f.label}]\n${v}` : ''
    })
    .filter(Boolean)
    .join('\n\n')
}

const toggleFreeForm = () => {
  if (!freeForm.value) {
    // 구조화 → 자유 서술: 작성 중이던 내용을 이어서 편집
    const composed = composeContent()
    if (composed && !form.value.content.trim()) form.value.content = composed
  }
  freeForm.value = !freeForm.value
}

// ── 이미지 첨부 ──
const addImages = (fileList) => {
  for (const file of fileList) {
    if (!file || !file.type?.startsWith('image/')) continue
    if (images.value.length >= MAX_IMAGES) {
      ElMessage.warning(`이미지는 최대 ${MAX_IMAGES}장까지 첨부할 수 있습니다.`)
      break
    }
    if (file.size > MAX_IMAGE_SIZE) {
      ElMessage.warning(`${file.name || '이미지'}: 장당 10MB 이하만 첨부할 수 있습니다.`)
      continue
    }
    images.value.push({ file, url: URL.createObjectURL(file) })
  }
}

const onFileChange = (e) => {
  addImages(Array.from(e.target.files || []))
  if (fileInput.value) fileInput.value.value = ''
}

const onPaste = (e) => {
  const items = Array.from(e.clipboardData?.items || [])
  const files = items
    .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
    .map((it) => it.getAsFile())
    .filter(Boolean)
  if (files.length) {
    e.preventDefault()
    addImages(files)
  }
}

const removeImage = (i) => {
  URL.revokeObjectURL(images.value[i].url)
  images.value.splice(i, 1)
}

const reset = () => {
  form.value = { feedback_type: 'IMPROVEMENT', title: '', content: '' }
  fields.value = {}
  freeForm.value = false
  images.value.forEach((img) => URL.revokeObjectURL(img.url))
  images.value = []
  if (fileInput.value) fileInput.value.value = ''
}

const submit = async () => {
  if (!form.value.title.trim()) {
    ElMessage.warning('제목을 입력하세요.')
    return
  }

  let content
  if (freeForm.value) {
    content = form.value.content.trim()
    if (!content) {
      ElMessage.warning('내용을 입력하세요.')
      return
    }
  } else {
    const missing = currentTemplate.value.find(
      (f) => f.required && !(fields.value[f.key] || '').trim()
    )
    if (missing) {
      ElMessage.warning(`'${missing.label}' 항목을 입력하세요.`)
      return
    }
    content = composeContent()
  }

  submitting.value = true
  try {
    const fd = new FormData()
    fd.append('feedback_type', form.value.feedback_type)
    fd.append('title', form.value.title.trim())
    fd.append('content', content)
    if (includeContext.value) {
      fd.append('page_route', route.fullPath)
      fd.append('user_agent', navigator.userAgent.slice(0, 300))
    }
    images.value.forEach((img) => fd.append('screenshots', img.file))

    await axiosInstance.post('/feedback/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    ElMessage.success('제보가 접수되었습니다. 처리 결과는 알림으로 안내됩니다.')
    reset()
    emit('update:modelValue', false)
    emit('submitted')
  } catch (e) {
    console.error('제보 제출 실패:', e)
    const data = e.response?.data
    ElMessage.error(data?.detail || data?.screenshots?.[0] || '제보 제출에 실패했습니다.')
  } finally {
    submitting.value = false
  }
}

defineExpose({ goMine: () => router.push('/feedback/mine') })
</script>

<style scoped>
.content-head {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 6px;
}
.content-label { font-size: 14px; color: #606266; }
.req { color: #f56c6c; }
.mode-toggle { font-size: 0.78rem; }
.tpl-item { margin-bottom: 10px; }
.tpl-item :deep(.el-form-item__label) { padding-bottom: 2px; font-size: 0.82rem; }

.img-area { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.img-list { display: flex; gap: 6px; flex-wrap: wrap; }
.img-thumb { position: relative; }
.thumb { width: 56px; height: 56px; border-radius: 6px; border: 1px solid #dcdfe6; display: block; }
.img-del {
  position: absolute; top: -6px; right: -6px;
  width: 18px; height: 18px; line-height: 15px; padding: 0;
  border: none; border-radius: 50%; background: #909399; color: #fff;
  font-size: 12px; cursor: pointer;
}
.img-del:hover { background: #f56c6c; }
.img-count { font-size: 0.75rem; color: #909399; }
</style>
